import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { isEmbedded } from "@/lib/noleggio";

export default function Login() {
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/";
  const nav = useNavigate();
  const { t } = useI18n();
  const { user, login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) nav(next, { replace: true });
  }, [user, next, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password || (mode === "register" && !name.trim())) return setError(t("fill_all"));
    setBusy(true);
    try {
      if (mode === "login") await login(email.trim(), password);
      else await register(name.trim(), email.trim(), password);
      toast.success(mode === "login" ? t("welcome_back") : t("account_created"));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    localStorage.setItem("mn_after_login", next);
    loginWithGoogle();
  };

  return (
    <div className="max-w-md mx-auto px-5 pt-10">
      <form onSubmit={submit} className="mn-card p-6 md:p-8 flex flex-col gap-4 mn-rise">
        <img src="/logo.png" alt="" className="w-20 h-20 object-contain" data-testid="login-logo" />
        <h1 className="text-3xl font-extrabold tracking-tight">{mode === "login" ? t("login") : t("register_title")}</h1>
        <p className="text-[var(--text-2)] -mt-2">{t("login_sub")}</p>
        <button type="button" data-testid="google-login-button" onClick={google} className="mn-btn mn-btn-ghost">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          {t("google")}
        </button>
        {isEmbedded() && <p className="text-xs text-[var(--muted)] -mt-2 text-center">{t("google_new_tab")}</p>}
        <div className="flex items-center gap-3 text-sm text-[var(--muted)]"><span className="flex-1 h-px bg-[var(--border)]" />{t("or")}<span className="flex-1 h-px bg-[var(--border)]" /></div>
        {mode === "register" && (
          <label className="flex flex-col gap-1.5"><span className="mn-label">{t("name")}</span>
            <input data-testid="register-name-input" className="mn-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("name_ph")} /></label>
        )}
        <label className="flex flex-col gap-1.5"><span className="mn-label">{t("email")}</span>
          <input data-testid="login-email-input" className="mn-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email_ph")} /></label>
        <label className="flex flex-col gap-1.5"><span className="mn-label">{t("password")}</span>
          <input data-testid="login-password-input" className="mn-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "register" ? t("pw_ph_register") : t("pw_ph_login")} /></label>
        {error && <p data-testid="login-error" className="text-sm font-semibold text-[var(--error)]">{error}</p>}
        <button data-testid="login-submit-button" className="mn-btn" disabled={busy}>
          {busy && <Loader2 size={18} className="animate-spin" />}{mode === "login" ? t("login") : t("register")}
        </button>
        <button type="button" data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }} className="text-sm text-[var(--text-2)] py-2">
          {mode === "login" ? t("no_account") : t("have_account")}
          <span className="font-bold text-[var(--brand-primary)]">{mode === "login" ? t("register") : t("login")}</span>
        </button>
      </form>
    </div>
  );
}
